import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import PrintIcon from '@mui/icons-material/Print';
import { Box, Button } from '@mui/material';
import { Grid2 as Grid } from '@mui/material';
import { useFeatureFlag } from '@submodule/hooks/useFeatureFlag';

import '../../assets/styles/vendor.css';
import './Policy.css';

import PageLoader from '../../components/pageloader/pageLoader';
import { PolicyAPI } from '../../features/Policy/api';
import ExportButton from '../../features/Policy/components/ExportButton';
import { isTenantSpecifiedStylesEnabled } from '../../features/Policy/const';
import { useDownloadPolicy } from '../../features/Policy/hooks';
import { useOrganizationId } from '../../features/Settings/hooks';

import './CustomCSS.scss';
import Attachments from './components/Attachments';
import ClinicalCriteria from './components/ClinicalCriteria';
import CodingNotes from './components/CodingNotes';
import MetaSection from './components/MetaSection';
import PolicyFooter from './components/PolicyFooter';
import PolicyHeader from './components/PolicyHeader';
import PolicyHistory from './components/PolicyHistory';
import PolicyTags from './components/PolicyTags';
import PolicyText from './components/PolicyText';
import ProcedureCodes from './components/ProcedureCodes';
import Questionnaire from './components/Questionnaire';
import References from './components/References';
import RequiredDocuments from './components/RequiredDocuments';

const getParamCaseInsensitive = (
  params: URLSearchParams,
  keyToFind: string
) => {
  for (const [key, value] of params.entries()) {
    if (key.toLowerCase() === keyToFind.toLowerCase()) {
      return value;
    }
  }
  return null;
};

const Policy = () => {
  const navigate = useNavigate();
  const { id, orgId, vid = 'active' } = useParams();
  const [policyData, setPolicyData] = React.useState<any>({});
  const [openLoader, setOpenLoader] = React.useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const lob = getParamCaseInsensitive(searchParams, 'lob');
  const service_date = getParamCaseInsensitive(searchParams, 'service_date');
  const theme = getParamCaseInsensitive(searchParams, 'theme');
  const { downloadFile } = useDownloadPolicy();

  const { organizationId, currentCodingHeadingTag } = useOrganizationId(orgId);

  const isTenantStylesEnabled = useFeatureFlag(
    isTenantSpecifiedStylesEnabled,
    false
  );

  const handleDownload = useCallback(async () => {
    try {
      setIsExportLoading(true);
      await downloadFile(orgId || '', id, vid);
    } finally {
      setIsExportLoading(false);
    }
  }, [downloadFile, id, orgId, vid]);

  React.useEffect(() => {
    if (!organizationId || !isTenantStylesEnabled) return;

    const tenantCssUrl = `${process.env.REACT_APP_MYPOLICIES_STYLES_URL}${encodeURIComponent(organizationId)}/policies/styles.css`;

    const link = document.createElement('link');

    link.rel = 'stylesheet';
    link.href = tenantCssUrl;

    link.onerror = () => {
      console.log('Custom tenant stylesheet missing');
    };

    document.head.appendChild(link);
  }, [organizationId, isTenantStylesEnabled]);

  useEffect(() => {
    const fetchPolicyData = async () => {
      setOpenLoader(true);
      try {
        const response = await PolicyAPI.getContent(
          orgId || '',
          id || '',
          vid,
          {
            lob,
            service_date,
          }
        );
        setPolicyData(response.data);
      } catch (error: any) {
        if (error?.response?.status === 404) {
          navigate('/not-found');
        }
      } finally {
        setOpenLoader(false);
      }
    };

    fetchPolicyData();
  }, [orgId, id, vid, navigate, service_date, lob]);

  useEffect(() => {
    if (
      lob &&
      policyData &&
      policyData?.procedures &&
      policyData?.procedures?.length > 0
    ) {
      const filteredPC = policyData?.procedures?.filter(
        (i: any) => i?.lob_title?.toLowerCase() === lob?.toLowerCase()
      );
      if (filteredPC.length === 0) {
        navigate('/not-found');
      }
    }
  }, [policyData, lob, navigate]);

  return (
    <Box
      component="section"
      className="headerBox"
      id={`tenantId-${orgId}`}
      style={{ minHeight: '75vh' }}
    >
      <Grid container className="container">
        <Grid
          size={12}
          className={`policy-template-content ck-content ${theme && theme.toLowerCase() === 'v2' ? 'theme-v2' : ''} `}
        >
          <div
            style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
          >
            <Button
              onClick={() => {
                window.print();
              }}
              style={{
                color: '#af68af',
                fontFamily: '"open_sanssemibold"',
                fontSize: '14px',
                textTransform: 'none',
              }}
            >
              <PrintIcon style={{ marginRight: '2px' }} /> Print
            </Button>
            <ExportButton
              loading={isExportLoading}
              menuItems={[{ label: 'ZIP', onClick: handleDownload }]}
            />
          </div>

          <div className="contentWrapper">
            <MetaSection policyShortName={id} policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <PolicyHeader policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <ClinicalCriteria policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <PolicyText policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <CodingNotes policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <ProcedureCodes lob={lob} policyData={policyData} currentCodingHeadingTag={currentCodingHeadingTag} />
          </div>

          <div className="contentWrapper">
            <RequiredDocuments policyData={policyData} />
          </div>

          {false && (
            <div className="contentWrapper">
              <Questionnaire
                policyData={policyData}
                setOpenLoader={setOpenLoader}
              />
            </div>
          )}

          <div className="contentWrapper">
            <Attachments
              policyData={policyData}
              setOpenLoader={setOpenLoader}
            />
          </div>

          <div className="contentWrapper">
            <References policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <PolicyTags policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <PolicyHistory policyData={policyData} />
          </div>

          <div className="contentWrapper">
            <PolicyFooter policyData={policyData} />
          </div>

          {/* Loader Overlay */}
          <PageLoader openLoader={openLoader} isGlobal={true} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Policy;
